import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGTMContext } from '../context/GTMContext';
import MobileOptimizedInput from '../components/MobileOptimizedInput';
import MobileOptimizedButton from '../components/MobileOptimizedButton';
import useWindowSize from '../hooks/useWindowSize';
import { getRegistroRef } from '../utils/registroOrigen';

const Register = () => {
  // Cargar datos guardados del localStorage al inicializar
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('registerFormData');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (error) {
        console.error('Error parsing saved form data:', error);
      }
    }
    return {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
    };
  });
  const [formError, setFormError] = useState('');
  const { register, loading } = useAuth();
  const gtm = useGTMContext();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const [searchParams] = useSearchParams();
  // Origen: ?ref=summer|liga, fallback por fecha
  const origen = searchParams.get('ref') || getRegistroRef();

  // Guardar datos en localStorage cada vez que cambien
  useEffect(() => {
    // Solo guardar si hay algún dato (evitar guardar formulario vacío)
    if (formData.name || formData.email || formData.phone) {
      localStorage.setItem('registerFormData', JSON.stringify(formData));
    }
  }, [formData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validación básica
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.phone) {
      setFormError('Por favor completa todos los campos');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Validación del teléfono (formato español)
    const phoneRegex = /^(\+34|0034|34)?[6789]\d{8}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      setFormError('Por favor introduce un número de teléfono válido');
      return;
    }

    try {
      // Eliminar confirmPassword antes de enviar, añadir origen
      const { confirmPassword: _confirmPassword, ...userData } = formData;
      await register({ ...userData, origen });

      // Enviar evento GTM de registro exitoso
      gtm.sendSignUpEvent('email', 'parent');

      // Limpiar datos guardados después del registro exitoso
      localStorage.removeItem('registerFormData');

      navigate('/help');
    } catch (error) {
      console.log('Error en registro - Error object:', error);
      console.log('Error en registro - Error message:', error.message);
      console.log('Error en registro - Error response:', error.response?.data);

      // Manejo mejorado de errores
      let errorMessage = 'Error al registrar usuario';

      if (error.response && error.response.data) {
        // Error del servidor con respuesta estructurada
        errorMessage = error.response.data.message || errorMessage;
        console.log('Error en registro - Usando response.data.message:', errorMessage);
      } else if (error.message) {
        // Error con mensaje directo
        errorMessage = error.message;
        console.log('Error en registro - Usando error.message:', errorMessage);
      }

      console.log('Error en registro - Mensaje final:', errorMessage);
      setFormError(errorMessage);
    }
  };

  return (
    <div className="auth-page">
      <div className="container">
        <div className="auth-container">
          <div className="auth-content">
            <div className="auth-header">
              <h1>Crear Cuenta de Usuario</h1>
              <p>Únete a ProKidsApp y disfruta de todas sus ventajas</p>
              {(formData.name || formData.email || formData.phone) && (
                <div style={{
                  fontSize: '12px',
                  color: '#28a745',
                  marginTop: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <span>💾</span>
                  <span>Datos del formulario guardados automáticamente</span>
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.removeItem('registerFormData');
                      setFormData({
                        name: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        phone: '',
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textDecoration: 'underline',
                      marginLeft: '10px'
                    }}
                  >
                    Limpiar
                  </button>
                </div>
              )}
            </div>

            {formError && <div className="alert alert-danger">{formError}</div>}

            <form onSubmit={handleSubmit} className="auth-form" style={{
              maxWidth: isMobile ? '100%' : '500px',
              margin: '0 auto',
              padding: isMobile ? '0' : '20px',
            }}>
              <MobileOptimizedInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                label="Nombre Completo"
                placeholder="Tu nombre completo"
                required
                autoComplete="name"
                autoCapitalize="words"
              />

              <MobileOptimizedInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                label="Correo Electrónico"
                placeholder="tu@email.com"
                required
                autoComplete="email"
                inputMode="email"
              />

              <MobileOptimizedInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                label="Teléfono de Contacto"
                placeholder="Ej: 612345678 o +34612345678"
                required
                autoComplete="tel"
                inputMode="tel"
                pattern="[0-9+\s-]*"
              />

              <MobileOptimizedInput
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                label="Contraseña"
                placeholder="Mínimo 6 caracteres"
                required
                autoComplete="new-password"
                minLength={6}
              />

              <MobileOptimizedInput
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                label="Confirmar Contraseña"
                placeholder="Repite tu contraseña"
                required
                autoComplete="new-password"
                minLength={6}
              />

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '24px',
                padding: isMobile ? '8px 0' : '4px 0',
              }}>
                <input
                  type="checkbox"
                  id="terms"
                  required
                  style={{
                    width: isMobile ? '20px' : '16px',
                    height: isMobile ? '20px' : '16px',
                    cursor: 'pointer',
                    marginTop: '2px',
                    flexShrink: 0,
                  }}
                />
                <label
                  htmlFor="terms"
                  style={{
                    fontSize: isMobile ? '14px' : '13px',
                    color: '#495057',
                    cursor: 'pointer',
                    userSelect: 'none',
                    lineHeight: '1.4',
                  }}
                >
                  Acepto los{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#4352A9',
                      textDecoration: 'none',
                      fontWeight: '500',
                    }}
                  >
                    Términos y Condiciones
                  </Link>{' '}
                  y la{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#4352A9',
                      textDecoration: 'none',
                      fontWeight: '500',
                    }}
                  >
                    Política de Privacidad
                  </Link>
                </label>
              </div>

              <MobileOptimizedButton
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                loading={loading}
                disabled={loading}
                icon="person_add"
                style={{ marginBottom: '24px' }}
              >
                {loading ? 'Registrando...' : 'Crear Cuenta'}
              </MobileOptimizedButton>
            </form>

            <div style={{
              textAlign: 'center',
              marginTop: '24px',
              padding: isMobile ? '16px 0' : '12px 0',
            }}>
              <p style={{
                fontSize: isMobile ? '16px' : '14px',
                color: '#6c757d',
                margin: '0',
              }}>
                ¿Ya tienes una cuenta?{' '}
                <Link
                  to="/login"
                  style={{
                    color: '#4352A9',
                    textDecoration: 'none',
                    fontWeight: '600',
                    padding: isMobile ? '8px 4px' : '4px',
                    minHeight: isMobile ? '44px' : 'auto',
                    display: isMobile ? 'inline-flex' : 'inline',
                    alignItems: 'center',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseOver={(e) => e.target.style.color = '#061156'}
                  onMouseOut={(e) => e.target.style.color = '#4352A9'}
                >
                  Inicia Sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
